import {
  BREAKPOINT_TABLET,
  FONT_SIZE_EXTRA_LARGE,
  FONT_SIZE_MEDIUM,
  FONT_SIZE_SMALL,
} from "constants/constants";
import styled from "styled-components/macro";
import {
  alertColor,
  darkGrey,
  darkModeTextColor,
  goodColor,
  textOnWatch,
} from "constants/colors";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import {
  useGetPageFromPath,
  useSelectedShift,
} from "utils/customHooks/genericHooks";
import { PredictedMetricIndicator } from "./PredictedMetricIndicator";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useIsDarkMode } from "store/atoms";
import { Process } from "types/types";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { clamp } from "lodash";
import { formatHoursMinutes } from "utils/timeUtils";
import { useMemo } from "react";

export const SingleGroupMetricValue = ({
  value,
  units,
  isPnlPopup,
  isPreferred,
  decimalPoints,
  formatHoursMinutes: shouldFormatHoursMinutes,
}: {
  value?: number | null;
  units?: string;
  isPnlPopup: boolean;
  isPreferred: boolean;
  decimalPoints?: number;
  formatHoursMinutes?: boolean;
}) => {
  const isStorybook = useIsStorybook();
  const { isTrendsPage } = useGetPageFromPath();
  const { isPredictedFutureMetric } = useSettings();
  const selectedShift = useSelectedShift();

  const shouldShowPredictedMetric = useMemo(
    () =>
      isPreferred &&
      !isTrendsPage &&
      isPredictedFutureMetric &&
      (selectedShift?.range?.lt > Date.now() || isStorybook),
    [
      isPreferred,
      isTrendsPage,
      isPredictedFutureMetric,
      selectedShift,
      isStorybook,
    ]
  );
  const [isDarkMode] = useIsDarkMode();

  const valueDisplay = useMemo(
    () =>
      value === undefined
        ? "--"
        : shouldFormatHoursMinutes
          ? formatHoursMinutes(Number(value))
          : value?.toFixed(clamp(decimalPoints ?? 0, 0, 6)),
    [value, shouldFormatHoursMinutes, decimalPoints]
  );
  return (
    <MetricValueStyles
      {...{ $isPnlPopup: isPnlPopup, $isDarkMode: isDarkMode }}
    >
      {valueDisplay}
      <div
        className="metricUnits"
        style={{ marginLeft: units === "%" ? 0 : "0.5ch" }}
      >
        {value === undefined ? null : units}
      </div>
      {shouldShowPredictedMetric && <PredictedMetricIndicator />}
    </MetricValueStyles>
  );
};

const MetricValueStyles = styled.div<{
  $isPnlPopup: boolean;
  $isDarkMode: boolean;
}>`
  position: relative;
  font-weight: ${props => (props.$isPnlPopup ? "normal" : "bold")};
  font-size: ${props =>
    props.$isPnlPopup ? FONT_SIZE_EXTRA_LARGE : FONT_SIZE_MEDIUM}px;
  color: ${({ $isPnlPopup, $isDarkMode }) =>
    $isDarkMode ? darkModeTextColor : $isPnlPopup ? textOnWatch : darkGrey};
  &.green {
    color: ${goodColor};
  }
  &.red {
    color: ${alertColor};
  }
  @media (min-width: ${BREAKPOINT_TABLET}px) {
    font-size: ${FONT_SIZE_SMALL}px;
  }
  display: flex;
  .metricUnits {
    font-weight: normal;
  }
`;

export const SingleGroupMetricTitle = ({
  displayName,
  overrideMachemetryIds,
  allProcesses,
}: {
  displayName: string;
  overrideMachemetryIds?: string[];
  allProcesses: Process[];
}) => {
  const overriddenTooltipText = useMemo(() => {
    const isOverridden =
      overrideMachemetryIds && overrideMachemetryIds.length > 0;
    const overriddenProcessNames =
      overrideMachemetryIds?.map(
        id => allProcesses.find(p => p.id === id)?.name ?? id
      ) ?? [];
    const overriddenTooltipText = isOverridden
      ? `* only for ${overriddenProcessNames?.join(", ")}`
      : "";
    return overriddenTooltipText;
  }, [allProcesses, overrideMachemetryIds]);

  const { translate } = useFlightTranslation();
  const { longestWordLength, displayNameWords } = useMemo(() => {
    const displayNameTranslated = translate(displayName);
    return {
      longestWordLength: Math.max(
        ...displayNameTranslated.split(" ").map(word => word.length)
      ),
      displayNameWords: displayNameTranslated.split(" "),
    };
  }, [translate, displayName]);

  return (
    <MetricTitleStyles
      className={`metricTitle`}
      {...{ $longestWordLength: longestWordLength }}
    >
      <div className="words">
        {displayNameWords.map((word, i) => (
          <div key={i} className="word">
            {word}
          </div>
        ))}
        {overriddenTooltipText ? "*" : ""}
      </div>
    </MetricTitleStyles>
  );
};

const MetricTitleStyles = styled.div<{ $longestWordLength: number }>`
  display: flex;
  align-items: center;
  position: relative;
  line-height: 1.2em;
  .words {
    max-width: ${props =>
      48 +
      (props.$longestWordLength > 6
        ? (props.$longestWordLength - 6) * 5
        : 0)}px;
    display: flex;
    flex-wrap: wrap;
    gap: 0 0.5ch;
    justify-content: center;
    width: fit-content;
  }
`;
